import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { MapPin, ShoppingBag, NotebookPen, Target, ArrowRight, Loader2, Lock } from "lucide-react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useUI } from "@/context/UIContext";
import { SiteHeader } from "@/components/SiteHeader";
import { Footer } from "@/components/BusinessSection";
import { MyHomeRangeDashboard } from "@/components/MyHomeRangeDashboard";

const QUICK = [
  { icon: MapPin, label: "Plan a state hunt", to: "/explore" },
  { icon: Target, label: "Points Planner", to: "/planner" },
  { icon: ShoppingBag, label: "Gear & Marketplace", to: "/gear" },
  { icon: NotebookPen, label: "Field Notes", to: "/field-notes" },
];

export default function Account() {
  const { isAuthed, ready, user } = useAuth();
  const { openAuth, setBuildHuntOpen } = useUI();
  const navigate = useNavigate();
  const [hunts, setHunts] = useState(null);

  useEffect(() => { if (isAuthed) api.hunts().then((d) => setHunts(Array.isArray(d) ? d : [])).catch(() => setHunts([])); }, [isAuthed]);

  return (
    <div className="bg-charcoal min-h-screen" data-testid="account-page">
      <SiteHeader />
      {!ready ? (
        <div className="py-32 grid place-items-center"><Loader2 className="animate-spin text-blaze" /></div>
      ) : !isAuthed ? (
        <div className="max-w-lg mx-auto text-center py-24 px-5" data-testid="account-gate">
          <span className="w-14 h-14 rounded-full bg-white/10 grid place-items-center mx-auto"><Lock className="text-blaze" /></span>
          <h1 className="font-serif text-4xl text-bone mt-5">My HomeRange</h1>
          <p className="text-bone/60 mt-3">Sign in to see your hunts, gear readiness and field notes in one place.</p>
          <button onClick={() => openAuth("login")} className="mt-7 bg-blaze text-white px-6 py-3 text-sm font-semibold uppercase tracking-wider hover:bg-blaze-light transition-colors" data-testid="account-signin">Sign in / Create account</button>
        </div>
      ) : (
        <main className="max-w-[1400px] mx-auto px-5 sm:px-10 py-12 text-bone">
          <span className="hr-overline text-blaze">My HomeRange</span>
          <h1 className="font-serif text-4xl sm:text-5xl tracking-tight mt-2">Welcome back, {(user.display_name || "hunter").split(" ")[0]}.</h1>

          <div className="grid lg:grid-cols-[1fr_auto] gap-8 mt-8 items-start">
            <div className="order-2 lg:order-1">
              <div className="flex items-center justify-between">
                <h2 className="font-serif text-2xl">Your hunts</h2>
                <button onClick={() => setBuildHuntOpen(true)} className="text-blaze text-sm font-semibold inline-flex items-center gap-1.5" data-testid="account-new-hunt">Build a hunt <ArrowRight size={14} /></button>
              </div>
              {hunts === null ? (
                <div className="py-10 grid place-items-center"><Loader2 className="animate-spin text-blaze" /></div>
              ) : hunts.length === 0 ? (
                <p className="text-bone/55 mt-4" data-testid="account-no-hunts">No saved hunts yet. Build your first hunt to unlock your readiness dashboard.</p>
              ) : (
                <div className="grid sm:grid-cols-2 gap-3 mt-4" data-testid="account-hunts">
                  {hunts.map((h) => (
                    <button key={h.id} onClick={() => navigate(`/marketplace?species=${encodeURIComponent(h.species || "")}&weapon=${encodeURIComponent(h.method || "")}&hunt=${encodeURIComponent(h.title || "")}`)}
                      className="text-left bg-charcoal-light border border-white/10 p-4 hover:border-blaze transition-colors" data-testid={`account-hunt-${h.id}`}>
                      <h3 className="font-serif text-xl">{h.title}</h3>
                      <p className="text-bone/55 text-sm mt-1">{[h.species, h.jurisdiction, h.method].filter(Boolean).join(" · ")}</p>
                      <span className="inline-flex items-center gap-1.5 text-blaze text-sm mt-3">Gear up <ShoppingBag size={13} /></span>
                    </button>
                  ))}
                </div>
              )}

              <h2 className="font-serif text-2xl mt-10">Quick links</h2>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mt-4">
                {QUICK.map((q) => {
                  const Icon = q.icon;
                  return (
                    <Link key={q.label} to={q.to} className="bg-charcoal-light border border-white/10 p-4 hover:border-blaze transition-colors" data-testid={`account-link-${q.to.replace(/\//g, "")}`}>
                      <Icon size={20} className="text-blaze" />
                      <p className="text-sm mt-3">{q.label}</p>
                    </Link>
                  );
                })}
              </div>
            </div>
            <div className="order-1 lg:order-2"><MyHomeRangeDashboard /></div>
          </div>
        </main>
      )}
      <Footer />
    </div>
  );
}
